import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { JOB_TITLE_CODE_PATTERN, NAME_MAX } from './hrApi'
import { createLeaveType, updateLeaveType, type LeaveType } from './leaveApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface Props {
  open: boolean
  /** `null` — yangi tur */
  type: LeaveType | null
  onClose: () => void
}

interface FormValues {
  code: string
  name: string
  paid: boolean
  annualDays: number | null
  usesBalance: boolean
  requiresApproval: boolean
  active: boolean
}

/**
 * Ta'til turi — katalog yozuvi.
 *
 * <h2>⚠️ «BALANSDAN YECHILADI» + YILLIK KUN — BIRGA</h2>
 * Server bu juftlikni majburlaydi: balansli tur yillik kunsiz bo'lsa
 * qoldiq HAR DOIM manfiy chiqardi. Panelda ham shu qoida bor va u
 * matn bilan aytiladi — server 400 gacha bormasdan.
 *
 * <h2>⚠️ O'CHIRISH YO'Q</h2>
 * `DELETE` backendda umuman yo'q: tur o'chirilsa o'tgan so'rovlar
 * nomsiz qolardi. O'rniga «faol» bayrog'i o'chiriladi — mavjud
 * so'rovlar joyida qoladi, yangisi esa bu turga yozilmaydi.
 *
 * <h2>⚠️ «TASDIQ TALAB QILINADI» O'CHIRILSA</h2>
 * So'rov DARHOL `APPROVED` bo'lib yaratiladi (`LeaveServiceImpl`) va
 * balansdan yechiladi. Bu ONGLI tanlov bo'lishi kerak, shuning uchun
 * izohda ochiq yozilgan.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * Yopishda `save.reset()`.
 */
export function LeaveTypeFormModal({ open, type, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      code: type?.code ?? '',
      name: type?.name ?? '',
      paid: type?.paid ?? true,
      annualDays: type?.annualDays ?? null,
      usesBalance: type?.usesBalance ?? true,
      requiresApproval: type?.requiresApproval ?? true,
      active: type?.active ?? true,
    })
  }, [open, type, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        paid: values.paid,
        annualDays: values.annualDays,
        usesBalance: values.usesBalance,
        requiresApproval: values.requiresApproval,
        active: values.active,
      }
      return type === null ? createLeaveType(draft) : updateLeaveType(type.id, draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={type === null ? t('hr.leaveType.createTitle') : t('hr.leaveType.editTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.leaveType.note')} fontSize={13} />
      </div>

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="code"
          label={t('hr.leaveType.code')}
          extra={t('hr.leaveType.codeHint')}
          rules={[
            { required: true, message: t('hr.leaveType.validation.codeRequired') },
            {
              validator: (_rule, value: string | undefined) =>
                !value || JOB_TITLE_CODE_PATTERN.test(value.trim().toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('hr.leaveType.validation.codeFormat'))),
            },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('hr.leaveType.name')}
          rules={[
            { required: true, message: t('hr.leaveType.validation.nameRequired') },
            { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="annualDays"
          label={t('hr.leaveType.annualDays')}
          extra={t('hr.leaveType.annualDaysHint')}
          dependencies={['usesBalance']}
          rules={[
            {
              validator: (_rule, value: number | null) => {
                if (value !== null && value !== undefined && value <= 0) {
                  return Promise.reject(new Error(t('hr.leaveType.validation.annualDaysPositive')))
                }
                // ⚠️ Server ham aynan shu juftlikni talab qiladi —
                // bu yerdagisi operatorni 400 gacha bormasdan to'xtatadi
                const uses = form.getFieldValue('usesBalance') as boolean
                if (uses && (value === null || value === undefined)) {
                  return Promise.reject(new Error(t('hr.leaveType.validation.annualDaysNeeded')))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="usesBalance"
          label={t('hr.leaveType.usesBalance')}
          extra={t('hr.leaveType.usesBalanceHint')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="paid"
          label={t('hr.leaveType.paid')}
          extra={t('hr.leaveType.paidHint')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="requiresApproval"
          label={t('hr.leaveType.requiresApproval')}
          extra={t('hr.leaveType.requiresApprovalHint')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="active"
          label={t('hr.leaveType.active')}
          extra={t('hr.leaveType.activeHint')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
